import api from '../services/api';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

export const fetchAboutContent = createAsyncThunk(
    'about/fetchAboutContent',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get('/about');
            const data = response.data;
            let aboutContent = Array.isArray(data) ? data[0] : data;

            // Parse characteristics if it's a string
            if (aboutContent && typeof aboutContent.characteristics === 'string') {
                try {
                    aboutContent.characteristics = JSON.parse(aboutContent.characteristics);
                } catch (e) {
                    aboutContent.characteristics = [];
                }
            }

            return aboutContent;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch about content');
        }
    }
);

export const createAboutContent = createAsyncThunk(
    'about/createAboutContent',
    async (formData, { rejectWithValue }) => {
        try {
            const response = await api.post('/about', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message || 'Failed to create about content');
        }
    }
);

export const updateAboutContent = createAsyncThunk(
    'about/updateAboutContent',
    async ({ id, formData }, { rejectWithValue }) => {
        try {
            const response = await api.put(`/about/${id}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message || 'Failed to update about content');
        }
    }
);

const aboutSlice = createSlice({
    name: 'about',
    initialState: {
        data: null,
        status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
        error: null,
        submitStatus: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
    },
    reducers: {
        resetSubmitStatus: (state) => {
            state.submitStatus = 'idle';
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch
            .addCase(fetchAboutContent.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(fetchAboutContent.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.data = action.payload;
            })
            .addCase(fetchAboutContent.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            })
            // Create
            .addCase(createAboutContent.pending, (state) => {
                state.submitStatus = 'loading';
            })
            .addCase(createAboutContent.fulfilled, (state, action) => {
                state.submitStatus = 'succeeded';
                state.data = action.payload;
            })
            .addCase(createAboutContent.rejected, (state, action) => {
                state.submitStatus = 'failed';
                state.error = action.payload;
            })
            // Update
            .addCase(updateAboutContent.pending, (state) => {
                state.submitStatus = 'loading';
            })
            .addCase(updateAboutContent.fulfilled, (state, action) => {
                state.submitStatus = 'succeeded';
                state.data = action.payload;
            })
            .addCase(updateAboutContent.rejected, (state, action) => {
                state.submitStatus = 'failed';
                state.error = action.payload;
            });
    },
});

export const { resetSubmitStatus } = aboutSlice.actions;

export const selectAboutData = (state) => state.about.data;
export const selectAboutStatus = (state) => state.about.status;
export const selectAboutSubmitStatus = (state) => state.about.submitStatus;
export const selectAboutError = (state) => state.about.error;

export default aboutSlice.reducer;
